import React, { useCallback, useMemo } from "react";
import { Stack } from "@certa/blocks/thanos";
import { css } from "emotion";
import { Select } from "@certa/catalyst/components/Select";
import { useIntl } from "react-intl";
import type { ObjectFilterConditionProps, ObjectField } from "./types";
import { ObjectFieldDropdown } from "./ObjectFieldDropdown";
import { RemoveIconButton } from "./ObjectFilterButtons";
import { getDefaultOperator, getOperatorsForField, isDateField } from "./utils";
import {
  OBJECT_FIELD_TYPES,
  ALL_OPERATORS,
  SMART_COLUMN_FIELDS
} from "./constants";
import type { ObjectFilterOperator } from "./constants";

import { TextInputField } from "../AdvancedFilters/AdvancedFilterCondition/ValueFieldInput/TextInputField";
import { NumberInputField } from "../AdvancedFilters/AdvancedFilterCondition/ValueFieldInput/NumberInputField";
import { DatePickerInputField } from "../AdvancedFilters/AdvancedFilterCondition/ValueFieldInput/DatePickerInputField";
import { RelativeDateInputField } from "../AdvancedFilters/AdvancedFilterCondition/ValueFieldInput/RelativeDateInputField";
import { RelativeDateField } from "../AdvancedFilters/AdvancedFilterCondition/RelativeDateField";
import { SelectValueInputField } from "../AdvancedFilters/AdvancedFilterCondition/ValueFieldInput/SelectValueInputField/SelectValueInputField";
import { FieldTypes } from "@certa/types";

const getFieldFromObjectTypeData = (
  fieldTag: string,
  objectTypeData?: ObjectFilterConditionProps["objectTypeData"]
): ObjectField | null => {
  const smartColumn = SMART_COLUMN_FIELDS.find(f => f.tag === fieldTag);
  if (smartColumn) {
    return {
      tag: smartColumn.tag,
      label: smartColumn.label,
      type: smartColumn.type
    };
  }

  if (!objectTypeData || !objectTypeData.schema?.properties?.[fieldTag]) {
    return null;
  }

  const schemaProperty = objectTypeData.schema?.properties?.[fieldTag];
  const fieldTitle =
    objectTypeData.translatedUiSchema?.fields?.[fieldTag]?.title;
  return {
    tag: fieldTag,
    label: fieldTitle || fieldTag,
    type: schemaProperty.type || "string"
  };
};

const getObjectFieldComponent = (
  fieldType: string,
  shouldUseRelativeDate = false
) => {
  switch (fieldType) {
    case OBJECT_FIELD_TYPES.number:
    case OBJECT_FIELD_TYPES.integer:
      return NumberInputField;
    case OBJECT_FIELD_TYPES.date:
    case OBJECT_FIELD_TYPES.dateTime:
      return shouldUseRelativeDate
        ? RelativeDateInputField
        : DatePickerInputField;
    case OBJECT_FIELD_TYPES.badgeTags:
      return SelectValueInputField;
    default:
      return TextInputField;
  }
};

const buildComponentProps = (
  fieldTag: string,
  baseProps: {
    value: string | string[];
    onChange: (value: string | string[] | undefined) => void;
    disabled: boolean;
  },
  selectedObjectType?: string
) => {
  switch (fieldTag) {
    case "badge_tags":
      return {
        ...baseProps,
        fieldTag: fieldTag,
        fieldType: FieldTypes.BADGE_TAGS,
        shouldUseNewLabelValueOptions: false,
        kindId: undefined,
        isMulti: true,
        contentType: "object",
        kindTag: selectedObjectType
      };
    default:
      return baseProps;
  }
};

const ValueInput: React.FC<{
  condition: ObjectFilterConditionProps["condition"];
  objectTypeData?: ObjectFilterConditionProps["objectTypeData"];
  onChange: (value: unknown) => void;
  disabled?: boolean;
  selectedObjectType?: string;
}> = ({
  condition,
  objectTypeData,
  onChange,
  disabled = false,
  selectedObjectType
}) => {
  const { field_tag: fieldTag, op, value, extra_json: extraJson } = condition;

  // Check if relative date mode is enabled via extra_json
  const isRelativeDateMode = !!extraJson?.is_relative_date;

  const isDisabled =
    !!ALL_OPERATORS.find(opDetail => opDetail.value === op)?.disableInput ||
    disabled;

  let field = getFieldFromObjectTypeData(fieldTag, objectTypeData);

  // If field not found in objectTypeData, check smart columns
  if (!field) {
    const smartColumn = SMART_COLUMN_FIELDS.find(f => f.tag === fieldTag);
    if (smartColumn) {
      field = {
        tag: smartColumn.tag,
        label: smartColumn.label,
        type: smartColumn.type
      };
    } else {
      // Final fallback
      field = {
        tag: fieldTag,
        type: "string",
        label: fieldTag
      };
    }
  }

  const FieldComponent = getObjectFieldComponent(
    field.type,
    isRelativeDateMode
  );

  const handleChange = (newValue: string | string[] | undefined) => {
    // Handle relative date values - add "d" suffix
    if (
      isRelativeDateMode &&
      newValue &&
      typeof newValue === "string" &&
      !Number.isNaN(Number(newValue))
    ) {
      onChange(`${newValue}d`);
      return;
    }

    // Handle array values for 'in' and 'not_in' operators
    if (op === "in" || op === "not_in") {
      if (typeof newValue === "string") {
        const values = newValue
          .split(",")
          .map(v => v.trim())
          .filter(Boolean);
        onChange(values);
      } else {
        onChange(newValue);
      }
    } else {
      onChange(newValue);
    }
  };

  // Parse relative date value - remove "d" suffix for display
  const displayValue =
    isRelativeDateMode &&
    value &&
    typeof value === "string" &&
    !Array.isArray(value)
      ? value.replace("d", "").trim()
      : value;

  const baseProps = {
    value: isRelativeDateMode
      ? (displayValue as string | string[])
      : (value as string | string[]),
    onChange: handleChange,
    disabled: isDisabled
  };

  const componentProps = buildComponentProps(
    fieldTag,
    baseProps,
    selectedObjectType
  );

  return React.createElement(FieldComponent as any, componentProps);
};

export const ObjectFilterCondition: React.FC<ObjectFilterConditionProps> = ({
  condition,
  conditionIndex,
  onUpdate,
  onRemove,
  objectTypeData,
  availableFields,
  canRemove = true,
  disabled = false,
  operatorsByFieldType,
  enhancementRules,
  hideSmartColumns,
  selectedObjectType
}) => {
  const intl = useIntl();

  const handleFieldChange = useCallback(
    (field: ObjectField | null) => {
      const currentExtraJson = condition.extra_json || {};

      const newCondition: ObjectFilterConditionProps["condition"] = {
        ...condition,
        field_tag: field?.tag || "",
        op: getDefaultOperator(),
        value: "",
        fieldType: field?.type || "",
        extra_json: {
          ...currentExtraJson,
          // Reset relative date flag when field changes, only keep it for date fields
          is_relative_date: isDateField(field?.type)
            ? currentExtraJson.is_relative_date
            : false
        }
      };

      // Apply enhancement rules only when field changes (field type may change)
      if (enhancementRules && field) {
        const rulesForFieldType =
          enhancementRules[field.type as keyof typeof enhancementRules];
        if (rulesForFieldType) {
          newCondition.extra_args = {
            ...(newCondition.extra_args || {}),
            ...rulesForFieldType
          };
        }
      }

      onUpdate(newCondition, conditionIndex);
    },
    [condition, enhancementRules, onUpdate, conditionIndex]
  );

  const handleOperatorChange = useCallback(
    (operator: string) => {
      const newCondition = {
        ...condition,
        op: operator as ObjectFilterOperator,
        value: "" // Reset value when operator changes
      };
      onUpdate(newCondition, conditionIndex);
    },
    [condition, conditionIndex, onUpdate]
  );

  const handleValueChange = useCallback(
    (value: unknown) => {
      const newCondition = {
        ...condition,
        value
      };
      onUpdate(newCondition, conditionIndex);
    },
    [condition, conditionIndex, onUpdate]
  );

  const handleRelativeDateChange = useCallback(
    (shouldUseRelativeDate: boolean) => {
      const newCondition = {
        ...condition,
        extra_json: {
          ...(condition.extra_json || {}),
          is_relative_date: shouldUseRelativeDate
        },
        value: "" // Reset value when toggling relative date mode
      };
      onUpdate(newCondition, conditionIndex);
    },
    [condition, conditionIndex, onUpdate]
  );

  const operatorOptions = useMemo(() => {
    return getOperatorsForField(
      condition.field_tag,
      availableFields,
      operatorsByFieldType
    );
  }, [condition.field_tag, availableFields, operatorsByFieldType]);

  const selectedField = useMemo(() => {
    // First check in availableFields (regular schema fields)
    let field = availableFields.find(f => f.tag === condition.field_tag);

    // If not found in availableFields, check smart columns
    if (!field && condition.field_tag) {
      const smartColumn = SMART_COLUMN_FIELDS.find(
        f => f.tag === condition.field_tag
      );
      if (smartColumn) {
        field = {
          tag: smartColumn.tag,
          label: smartColumn.label,
          type: smartColumn.type
        };
      }
    }

    return field || null;
  }, [condition.field_tag, availableFields]);

  return (
    <Stack
      direction="vertical"
      gap="s2"
      className={fieldsWrapperCss}
      role="gridcell"
    >
      <Stack gap="s2" align="center">
        <ObjectFieldDropdown
          fieldOptions={availableFields}
          selectedField={selectedField}
          onFieldSelect={handleFieldChange}
          disabled={disabled}
          hideSmartColumns={hideSmartColumns}
        />

        <div className={operatorWrapperStyle}>
          <Select
            label="Operator"
            options={operatorOptions}
            value={condition.op || ""}
            onChange={handleOperatorChange}
            disabled={!condition.field_tag || disabled}
            width="100%"
            isSearchable={false}
            isClearable={false}
            menuPortalTarget={document.body}
          />
        </div>

        <ValueInput
          condition={condition}
          objectTypeData={objectTypeData}
          onChange={handleValueChange}
          disabled={disabled}
          selectedObjectType={selectedObjectType}
        />

        {canRemove && (
          <RemoveIconButton
            onClick={() => onRemove(conditionIndex)}
            ariaLabel={intl.formatMessage({
              id: "report.removeCondition",
              defaultMessage: "Remove condition"
            })}
            disabled={disabled}
          />
        )}
      </Stack>

      {/* Show relative date checkbox for date fields */}
      {isDateField(selectedField?.type) && (
        <RelativeDateField
          value={!!condition.extra_json?.is_relative_date}
          onChange={handleRelativeDateChange}
        />
      )}
    </Stack>
  );
};

const fieldsWrapperCss = css({
  flexGrow: 1,
  minWidth: "524px",
  maxWidth: "700px"
});

/**
 * Have a min-width of 120px for the operator field
 * so that single words like 'contains' don't break to next line
 */
const operatorWrapperStyle = css({
  minWidth: "120px"
});
